import { DeleteRequest } from '../types';

interface DeleteRequestStats {
  totalRequests: number;
  pendingRequests: number;
  approvedThisMonth: number;
  rejectedThisMonth: number;
  userRequests: number;
  providerRequests: number;
}

interface TransformedDeleteRequestsResponse {
  requests: DeleteRequest[];
  total: number;
  page: number;
  totalPages: number;
  stats?: DeleteRequestStats;
}

const mapRawRequestToDeleteRequest = (raw: any): DeleteRequest => {
  // Extract email from nested details object (based on actual API response)
  const email = raw.details?.email || raw.email || 'No email provided';

  // Extract reason from root level
  const reason = raw.reason || raw.deletionReason || '';

  // Extract name from nested details object
  const name = raw.details?.name || raw.name || 'Unknown';

  return {
    requestId: raw._id || raw.requestId || raw.id,
    userId: raw.userId,
    providerId: raw.providerId,
    accountType: raw.type || raw.accountType,
    name,
    email,
    reason,
    status: raw.status,
    rejectionReason: raw.rejectionReason,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    profileSnapshot: raw.profileSnapshot || {
      phone: raw.details?.profile?.phone || raw.details?.phone || raw.phone,
      address: raw.details?.address || raw.address,
      city: raw.details?.city || raw.city,
    },
  };
};

export const transformDeleteRequestsResponse = (data: any): TransformedDeleteRequestsResponse => {
  let rawRequests: any[] = [];

  if (Array.isArray(data)) {
    rawRequests = data;
  } else if (Array.isArray(data.data)) {
    rawRequests = data.data;
  } else if (data.requests) {
    rawRequests = data.requests;
  } else if (data.deleteRequests) {
    rawRequests = data.deleteRequests;
  }

  const requests = rawRequests.map(mapRawRequestToDeleteRequest);

  const total = data.total !== undefined ? data.total : requests.length;
  const page = data.page !== undefined ? data.page : 1;
  const totalPages = data.totalPages !== undefined ? data.totalPages : (total > 0 ? 1 : 0);

  const result: TransformedDeleteRequestsResponse = {
    requests,
    total,
    page,
    totalPages,
  };

  if (data.stats) {
    result.stats = {
      totalRequests: data.stats.totalRequests || 0,
      pendingRequests: data.stats.pendingRequests || 0,
      approvedThisMonth: data.stats.approvedThisMonth || 0,
      rejectedThisMonth: data.stats.rejectedThisMonth || 0,
      userRequests: data.stats.userRequests || 0,
      providerRequests: data.stats.providerRequests || 0,
    };
  } else if (requests.length > 0) {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    let pendingCount = 0;
    let approvedThisMonthCount = 0;
    let rejectedThisMonthCount = 0;
    let userRequestsCount = 0;
    let providerRequestsCount = 0;

    requests.forEach((request: any) => {
      if (request.status === 'pending') {
        pendingCount++;
      }

      const createdDate = new Date(request.createdAt || request.updatedAt);
      const requestMonth = createdDate.getMonth();
      const requestYear = createdDate.getFullYear();

      if (requestMonth === currentMonth && requestYear === currentYear) {
        if (request.status === 'approved') {
          approvedThisMonthCount++;
        } else if (request.status === 'rejected') {
          rejectedThisMonthCount++;
        }
      }

      if (request.type === 'user') {
        userRequestsCount++;
      } else if (request.type === 'provider') {
        providerRequestsCount++;
      }
    });

    result.stats = {
      totalRequests: requests.length,
      pendingRequests: pendingCount,
      approvedThisMonth: approvedThisMonthCount,
      rejectedThisMonth: rejectedThisMonthCount,
      userRequests: userRequestsCount,
      providerRequests: providerRequestsCount,
    };
  }

  return result;
};
